import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import api from '@/lib/api'
import { IS_MOCK_MODE, STORAGE_KEYS, readJSON, writeJSON } from '@/lib/mockMode'
import { overageRateCents } from '@/data/pricing'
import { usePlanStore } from './planStore'

function loadStoredQuota() {
  return readJSON(STORAGE_KEYS.quota, null)
}

function persistQuota(snapshot) {
  writeJSON(STORAGE_KEYS.quota, snapshot)
}

function nextResetIso() {
  const d = new Date()
  d.setUTCMonth(d.getUTCMonth() + 1, 1)
  d.setUTCHours(0, 0, 0, 0)
  return d.toISOString()
}

// Pricing v3: plan pages + consent-gated metered overage. Overage is OFF
// until the user sets a monthly budget; alerts fire at a selectable
// threshold of both the plan quota and the overage budget.
export const useQuotaStore = defineStore('quota', () => {
  const stored = loadStoredQuota()

  const pagesUsed = ref(stored?.pagesUsed ?? 0)
  const overagePagesPeriod = ref(stored?.overagePagesPeriod ?? 0)
  const overageLimitCents = ref(stored?.overageLimitCents ?? null) // null = overage off
  const alertThresholdPct = ref(stored?.alertThresholdPct ?? 80)
  const digestOptOut = ref(stored?.digestOptOut ?? false)
  const resultEmailEnabled = ref(stored?.resultEmailEnabled ?? false)
  const resultEmailDest = ref(stored?.resultEmailDest ?? null)
  const periodResetDate = ref(stored?.periodResetDate || nextResetIso())
  // E3 (Enterprise): 'team' when the user draws on a pooled org quota. The
  // server-reported pool limit overrides the static tier limit (it grows
  // with seat blocks).
  const scope = ref(stored?.scope ?? 'user')
  const teamId = ref(stored?.teamId ?? null)
  const teamRole = ref(stored?.teamRole ?? null)
  const serverPagesLimit = ref(stored?.serverPagesLimit ?? null)
  const isLoading = ref(false)
  const error = ref('')
  const lastFetchedAt = ref(stored?.lastFetchedAt || null)

  const planStore = usePlanStore()

  const tier = computed(() => planStore.tier)
  const pagesLimit = computed(() => serverPagesLimit.value ?? planStore.pagesLimit)
  const overageRate = computed(() => overageRateCents(planStore.tier))

  const percentUsed = computed(() => {
    const limit = pagesLimit.value || 1
    return Math.min(100, Math.round((pagesUsed.value / limit) * 100))
  })
  const remainingPlanPages = computed(() => Math.max(0, pagesLimit.value - pagesUsed.value))
  const overageEnabled = computed(() => overageRate.value !== null && overageLimitCents.value !== null)
  const overageSpendCents = computed(() => overagePagesPeriod.value * (overageRate.value ?? 0))
  const budgetRemainingCents = computed(() =>
    overageEnabled.value ? Math.max(0, overageLimitCents.value - overageSpendCents.value) : 0)
  const isUsingOverage = computed(() => overagePagesPeriod.value > 0)
  const isExhausted = computed(() =>
    remainingPlanPages.value <= 0 && (!overageEnabled.value || budgetRemainingCents.value <= 0))
  // Alert states at the user's threshold (in-app Phase A).
  const planAlert = computed(() => percentUsed.value >= alertThresholdPct.value)
  const budgetAlert = computed(() =>
    overageEnabled.value && overageLimitCents.value > 0 &&
    (overageSpendCents.value / overageLimitCents.value) * 100 >= alertThresholdPct.value)
  const isNearLimit = computed(() => planAlert.value)

  function _persist() {
    persistQuota({
      pagesUsed: pagesUsed.value,
      overagePagesPeriod: overagePagesPeriod.value,
      overageLimitCents: overageLimitCents.value,
      alertThresholdPct: alertThresholdPct.value,
      digestOptOut: digestOptOut.value,
      resultEmailEnabled: resultEmailEnabled.value,
      resultEmailDest: resultEmailDest.value,
      periodResetDate: periodResetDate.value,
      lastFetchedAt: lastFetchedAt.value,
      scope: scope.value,
      teamId: teamId.value,
      teamRole: teamRole.value,
      serverPagesLimit: serverPagesLimit.value,
    })
  }

  async function fetchQuota() {
    isLoading.value = true
    error.value = ''
    try {
      if (IS_MOCK_MODE) {
        if (!periodResetDate.value) periodResetDate.value = nextResetIso()
        lastFetchedAt.value = new Date().toISOString()
        _persist()
        return { tier: tier.value, pagesUsed: pagesUsed.value, pagesLimit: pagesLimit.value }
      }

      const { data } = await api.get('/billing/quota')
      pagesUsed.value = data.pagesUsed ?? 0
      overagePagesPeriod.value = data.overagePagesPeriod ?? 0
      overageLimitCents.value = data.overageLimitCents ?? null
      alertThresholdPct.value = data.alertThresholdPct ?? 80
      digestOptOut.value = !!data.digestOptOut
      resultEmailEnabled.value = !!data.resultEmailEnabled
      resultEmailDest.value = data.resultEmailDest ?? null
      periodResetDate.value = data.periodResetDate || nextResetIso()
      scope.value = data.scope || 'user'
      teamId.value = data.teamId ?? null
      teamRole.value = data.teamRole ?? null
      serverPagesLimit.value = data.scope === 'team' ? (data.pagesLimit ?? null) : null
      lastFetchedAt.value = new Date().toISOString()
      planStore.applyServerState({
        tier: data.tier,
        billingInterval: data.billingInterval,
        status: data.subscriptionStatus,
        endsAt: data.endsAt,
        polarCustomerId: data.polarCustomerId,
        polarSubscriptionId: data.polarSubscriptionId,
        billingRail: data.billingRail,
      })
      _persist()
      return data
    } catch (err) {
      error.value = err?.response?.data?.error || err?.message || 'Failed to fetch quota'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Mirror of the backend deduction: plan pages first, overflow → overage.
  function usePages(n = 1) {
    const fromPlan = Math.min(n, remainingPlanPages.value)
    pagesUsed.value += fromPlan
    const overflow = n - fromPlan
    if (overflow > 0) overagePagesPeriod.value += overflow
    _persist()
  }

  async function updateUsageSettings({ limitCents, thresholdPct, digestOptOut: optOut, resultEmailEnabled: emailEnabled, resultEmailDest: emailDest }) {
    if (IS_MOCK_MODE) {
      if (limitCents !== undefined) overageLimitCents.value = limitCents
      if (thresholdPct !== undefined) alertThresholdPct.value = thresholdPct
      if (optOut !== undefined) digestOptOut.value = optOut
      if (emailEnabled !== undefined) resultEmailEnabled.value = emailEnabled
      if (emailDest !== undefined) resultEmailDest.value = emailDest || null
      _persist()
      return
    }
    const { data } = await api.put('/billing/usage-settings', {
      ...(limitCents !== undefined ? { overageLimitCents: limitCents } : {}),
      ...(thresholdPct !== undefined ? { alertThresholdPct: thresholdPct } : {}),
      ...(optOut !== undefined ? { digestOptOut: optOut } : {}),
      ...(emailEnabled !== undefined ? { resultEmailEnabled: emailEnabled } : {}),
      ...(emailDest !== undefined ? { resultEmailDest: emailDest } : {}),
    })
    overageLimitCents.value = data.overageLimitCents ?? null
    alertThresholdPct.value = data.alertThresholdPct ?? 80
    digestOptOut.value = !!data.digestOptOut
    resultEmailEnabled.value = !!data.resultEmailEnabled
    resultEmailDest.value = data.resultEmailDest ?? null
    _persist()
  }

  function setUsed(n) {
    pagesUsed.value = Math.max(0, n)
    _persist()
  }

  // Mock mode: local zero. Real mode: staging-only backend reset (the route
  // is allowlisted server-side and 404s everywhere else).
  async function resetUsage() {
    if (IS_MOCK_MODE) {
      pagesUsed.value = 0
      overagePagesPeriod.value = 0
      periodResetDate.value = nextResetIso()
      _persist()
      return
    }
    await api.post('/billing/reset-quota')
    await fetchQuota()
  }

  return {
    tier,
    scope,
    teamId,
    teamRole,
    pagesUsed,
    pagesLimit,
    overagePagesPeriod,
    overageLimitCents,
    alertThresholdPct,
    digestOptOut,
    resultEmailEnabled,
    resultEmailDest,
    overageRate,
    periodResetDate,
    isLoading,
    error,
    lastFetchedAt,
    percentUsed,
    remainingPlanPages,
    overageEnabled,
    overageSpendCents,
    budgetRemainingCents,
    isUsingOverage,
    isExhausted,
    isNearLimit,
    planAlert,
    budgetAlert,
    isMockMode: IS_MOCK_MODE,
    fetchQuota,
    usePages,
    updateUsageSettings,
    setUsed,
    resetUsage,
  }
})
